import { getActions } from './common/common.actions';
import { CommonState, initialState } from '@redux/common/common.state';
import { Injectable } from '@angular/core';
import { CommonEffects } from '@redux/common/common.effects';
import { getReducer } from '@redux/common/common.reducer';
import { getSelectors } from '@redux/common/common.selector';
import { Action, createAction, props } from '@ngrx/store';
import type { ServiceRequestFormModel } from '@models/forms/service-request-form.model';
import { createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, Observable, of } from 'rxjs';
import { ServiceRequest } from '@models/service-requests/service-request.model';
import { ServiceType } from '@models/service-requests/service-type.model';

export const serviceRequestsStateKey = 'ServiceRequestsState';

export interface ServiceRequestsState extends CommonState<ServiceRequest> {
  serviceTypes: ServiceType[];
  serviceTypesLoading: boolean;
  serviceTypesError: string | null;
}

export const serviceRequestsState: ServiceRequestsState = {
  ...initialState<ServiceRequest>(),
  serviceTypes: [],
  serviceTypesLoading: false,
  serviceTypesError: null,
};
//
export const serviceRequestsActions = {
  ...getActions<ServiceRequest>(serviceRequestsStateKey),
  createService: createAction(
    `[${serviceRequestsStateKey}]CreateService`,
    props<{ model: ServiceRequestFormModel }>(),
  ),
  createServiceSuccess: createAction(
    `[${serviceRequestsStateKey}]CreateServiceSuccess`,
    props<{ model: ServiceRequestFormModel }>(),
  ),
  createServiceFail: createAction(
    `[${serviceRequestsStateKey}]CreateServiceFail`,
    props<{ error: string }>(),
  ),
  // Service Types actions
  loadServiceTypes: createAction(`[${serviceRequestsStateKey}]LoadServiceTypes`),
  loadServiceTypesSuccess: createAction(
    `[${serviceRequestsStateKey}]LoadServiceTypesSuccess`,
    props<{ serviceTypes: ServiceType[] }>(),
  ),
  loadServiceTypesFail: createAction(
    `[${serviceRequestsStateKey}]LoadServiceTypesFail`,
    props<{ error: string }>(),
  ),
  //
};
//
export const selectServiceRequestsBase = getSelectors<ServiceRequest>(serviceRequestsStateKey);
export const selectServiceRequests = {
  ...selectServiceRequestsBase,
  serviceTypes: (state: ServiceRequestsState) => state.serviceTypes,
  serviceTypesLoading: (state: ServiceRequestsState) => state.serviceTypesLoading,
  serviceTypesError: (state: ServiceRequestsState) => state.serviceTypesError,
};

// Create base reducer
const baseReducer = getReducer<ServiceRequest>(serviceRequestsState, serviceRequestsActions, []);

// Create custom reducer that includes base functionality
export const serviceRequestsReducers = (state: ServiceRequestsState = serviceRequestsState, action: Action): ServiceRequestsState => {
  // First apply base reducer
  const baseState = baseReducer(state, action) as ServiceRequestsState;
  
  // Ensure we always have ServiceRequestsState properties
  const currentState: ServiceRequestsState = {
    ...baseState,
    serviceTypes: baseState.serviceTypes || state.serviceTypes || [],
    serviceTypesLoading: baseState.serviceTypesLoading !== undefined ? baseState.serviceTypesLoading : state.serviceTypesLoading || false,
    serviceTypesError: baseState.serviceTypesError !== undefined ? baseState.serviceTypesError : state.serviceTypesError || null,
  };
  
  // Then apply our custom reducers
  switch (action.type) {
    case serviceRequestsActions.loadServiceTypes.type:
      return {
        ...currentState,
        serviceTypes: currentState.serviceTypes,
        serviceTypesLoading: true,
        serviceTypesError: null,
      };
    
    case serviceRequestsActions.loadServiceTypesSuccess.type: {
      const successAction = action as ReturnType<typeof serviceRequestsActions.loadServiceTypesSuccess>;
      return {
        ...currentState,
        serviceTypes: successAction.serviceTypes,
        serviceTypesLoading: false,
        serviceTypesError: null,
      };
    }
    
    case serviceRequestsActions.loadServiceTypesFail.type: {
      const failAction = action as ReturnType<typeof serviceRequestsActions.loadServiceTypesFail>;
      return {
        ...currentState,
        serviceTypes: currentState.serviceTypes,
        serviceTypesLoading: false,
        serviceTypesError: failAction.error,
      };
    }
    
    default:
      return currentState;
  }
};

@Injectable()
export class ServiceRequestsEffects extends CommonEffects<ServiceRequest> {
  constructor() {
    super();
    Object.assign(this, this.createEffects(serviceRequestsActions, selectServiceRequests));
  }

  createService$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(serviceRequestsActions.createService),
      mergeMap(({ model }) => {
        return this.api.post<ServiceRequestFormModel>(`v2/service-requests`, model).pipe(
          map((createdModel) =>
            serviceRequestsActions.createServiceSuccess({ model: createdModel }),
          ),
          catchError((error) =>
            of(serviceRequestsActions.createServiceFail({ error: error.message })),
          ),
        );
      }),
    );
  });

  loadServiceTypes$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(serviceRequestsActions.loadServiceTypes),
      mergeMap(() => {
        return this.api.get<ServiceType[]>(`v2/service-requests/types`).pipe(
          map((serviceTypes) =>
            serviceRequestsActions.loadServiceTypesSuccess({ serviceTypes }),
          ),
          catchError((error) =>
            of(serviceRequestsActions.loadServiceTypesFail({ error: error.message })),
          ),
        );
      }),
    );
  });
}
